import { useState } from "react";
import { Box, Button, Flex, HStack, Icon, Stack } from "@chakra-ui/react";
import CDS from "design-system";
import { LuCalendarClock, LuCalendarPlus, LuArrowLeft, LuUsers } from "react-icons/lu";
import { Heading18 } from "components/Heading";
import { Text14 } from "components/Text";
import { MeetingId, CalendarServiceEvent } from "features/Resources/Meeting/models";
import {
  useAvailableSessionsQuery,
  useAddToSession,
  useMeetingDetailQuery,
  useScheduleMeeting,
} from "features/Resources/Meeting/hooks";
import ScheduleMeetingForm from "features/Resources/Meeting/Components/Form/ScheduleMeetingForm";
import SelectFromMeetingList from "features/Resources/Meeting/Components/SelectFromMeetingList";
import { TertiaryButton } from "components/Button";

type View = "menu" | "reschedule" | "pick_another" | "create_new";

interface GroupSessionRescheduleOptionsProps {
  meetingId: MeetingId;
  onClose: () => void;
}

interface OptionCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  onClick: () => void;
}

function OptionCard({ icon, title, description, onClick }: OptionCardProps) {
  return (
    <Box
      as="button"
      w="100%"
      p="4"
      borderWidth="1px"
      borderRadius="md"
      borderColor="gray.200"
      _hover={{ borderColor: "blue.400", bg: "blue.50" }}
      transition="all 0.15s"
      textAlign="left"
      onClick={onClick}
    >
      <HStack spacing="3" align="flex-start">
        <Icon as={icon} boxSize="5" color="blue.500" mt="0.5" />
        <Box>
          <CDS.Text14 fontWeight="600">
            {title}
          </CDS.Text14>
          <Text14 color="gray.600">{description}</Text14>
        </Box>
      </HStack>
    </Box>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" leftIcon={<Icon as={LuArrowLeft} />} onClick={onClick} mb="2">
      Back
    </Button>
  );
}

function PickAnotherSessionButton({
  meetingId,
  event,
  onSuccess,
}: {
  meetingId: MeetingId;
  event: CalendarServiceEvent;
  onSuccess: () => void;
}) {
  const { mutate: addToSession, isPending } = useAddToSession(meetingId);
  return (
    <TertiaryButton h="28px" isLoading={isPending} onClick={() => addToSession({ eventId: event.id }, { onSuccess })}>
      Select
    </TertiaryButton>
  );
}

export default function GroupSessionRescheduleOptions({ meetingId, onClose }: GroupSessionRescheduleOptionsProps) {
  const [view, setView] = useState<View>("menu");
  const { data: meeting } = useMeetingDetailQuery(meetingId);
  useAvailableSessionsQuery(meetingId); // prefetch for SelectFromMeetingList
  const { mutate: onScheduleMeeting, isPending: isScheduling } = useScheduleMeeting(meetingId);

  const isRecurring = meeting?.isRecurring ?? false;

  if (view === "reschedule" || view === "create_new") {
    return (
      <Stack mt="4">
        <BackButton onClick={() => setView("menu")} />
        <Heading18>{view === "reschedule" ? "Reschedule This Session" : "Create a New Session"}</Heading18>
        <ScheduleMeetingForm
          meetingId={meetingId}
          onComplete={onClose}
          onSubmit={(params) => onScheduleMeeting(params, { onSuccess: onClose })}
          submitIsLoading={isScheduling}
        />
      </Stack>
    );
  }

  if (view === "pick_another") {
    return (
      <Stack mt="4">
        <BackButton onClick={() => setView("menu")} />
        <Heading18>Pick Another Session</Heading18>
        <Text14>Select an existing session for this new hire to join.</Text14>
        <SelectFromMeetingList
          meetingId={meetingId}
          renderAction={(event) => <PickAnotherSessionButton meetingId={meetingId} event={event} onSuccess={onClose} />}
        />
        <Flex w="100%" justify="flex-end" mt="2">
          <Button onClick={onClose}>Close</Button>
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack mt="4" spacing="3">
      <OptionCard
        icon={LuCalendarClock}
        title="Reschedule this session"
        description="Move this session to a different time. All attendees will be moved together."
        onClick={() => setView("reschedule")}
      />
      <OptionCard
        icon={LuUsers}
        title="Pick another session"
        description="Join a different existing session for this meeting."
        onClick={() => setView("pick_another")}
      />
      {!isRecurring && (
        <OptionCard
          icon={LuCalendarPlus}
          title="Create a new session"
          description="Schedule a brand new session at a different time."
          onClick={() => setView("create_new")}
        />
      )}
      <Flex w="100%" justify="flex-end" mt="1">
        <Button onClick={onClose}>Close</Button>
      </Flex>
    </Stack>
  );
}
